import { Box, Loader, Center } from '@mantine/core';
import { Listing, IPopulatedListing } from '../types/Listing.model';
import { useEffect, useState, type CSSProperties } from 'react';
import { IAppPublic } from '../types/App.model';
import {
  getListableApps,
  getAppsByUser,
  createListing,
  getListingsByProductId,
  updateListingConfirmation,
} from '../Api';
import { useStore } from '../store/Store';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { IconCalendar, IconClock, IconCheck, IconTrash, IconSend } from '@tabler/icons-react';
import { colorWithAlpha } from '@/utils/colorWithAlpha';
import { DEFAULT_QR_CODE_COLOR } from '@/theme';
import { EditorChipButton } from '@/components/EditorChipButton';
import classes from './ProductListingManager.module.css';

type ProductListingManagerProps = {
  id: string;
};

function getListingStatusChipClass(status: string): string {
  switch (status) {
    case Listing.Status.LISTED:
      return classes.chipListed;
    case Listing.Status.PENDING:
      return classes.chipPending;
    case Listing.Status.UNLISTED:
      return classes.chipDeclined;
    default:
      return classes.chipNeutral;
  }
}

function getConfirmationChipClass(confirmation: string): string {
  switch (confirmation) {
    case Listing.Confirmation.CONFIRMED:
      return classes.chipListed;
    case Listing.Confirmation.PENDING:
      return classes.chipPending;
    case Listing.Confirmation.DECLINED:
      return classes.chipDeclined;
    default:
      return classes.chipNeutral;
  }
}

export function ProductListingManager({ id: productObjId }: ProductListingManagerProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [listings, setListings] = useState<IPopulatedListing[]>([]);
  const [apps, setApps] = useState<IAppPublic[]>([]);
  const [adminAppIds, setAdminAppIds] = useState<Set<string>>(new Set());
  const userId = useStore((state) => state.userProfile._id);
  const color: string = useStore((state) => state.userProfile.qrCodeColor) || DEFAULT_QR_CODE_COLOR;

  const accentStyle = {
    '--listing-accent': color,
    '--listing-accent-soft': colorWithAlpha(color, 0.08),
    '--listing-accent-border': colorWithAlpha(color, 0.22),
  } as CSSProperties;

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [publicApps, productListings, administeredApps] = await Promise.all([
          getListableApps(),
          getListingsByProductId(productObjId),
          userId ? getAppsByUser(userId) : Promise.resolve([]),
        ]);

        setApps(publicApps);
        setListings(productListings);
        setAdminAppIds(new Set(administeredApps.map((app) => app._id)));
      } catch (error) {
        console.error('Error fetching data:', error);
        setApps([]);
        setListings([]);
        setAdminAppIds(new Set());
      } finally {
        setIsLoading(false);
      }
    };

    if (productObjId) {
      fetchData();
    } else {
      setIsLoading(false);
    }
  }, [productObjId, userId]);

  const handleRequestListing = async (appId: string) => {
    setIsLoading(true);
    try {
      if (!userId) {
        notifications.show({
          title: t('editProduct.editError'),
          message: t('editProduct.userNotLoggedIn'),
          color: 'red',
        });
        return;
      }

      await createListing(productObjId, appId, userId);
      const updatedListings = await getListingsByProductId(productObjId);
      setListings(updatedListings);

      notifications.show({
        title: t('editProduct.editSuccess'),
        message: t('editProduct.listingRequestedSuccess'),
        color: 'green',
      });
    } catch (error) {
      console.error('Error requesting listing:', error);
      notifications.show({
        title: t('editProduct.editError'),
        message: t('editProduct.listingError'),
        color: 'red',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateConfirmation = async (listingId: string, isConfirmed: boolean) => {
    setIsLoading(true);
    try {
      if (!userId) {
        notifications.show({
          title: t('editProduct.editError'),
          message: t('editProduct.userNotLoggedIn'),
          color: 'red',
        });
        return;
      }

      const result = await updateListingConfirmation(listingId, userId, false, isConfirmed);

      if (result === null) {
        setListings((prevListings) => prevListings.filter((l) => l._id !== listingId));
        notifications.show({
          title: t('editProduct.editSuccess'),
          message: t('editProduct.listingRemoved'),
          color: 'green',
        });
        setIsLoading(false);
        return;
      }

      const updatedListings = await getListingsByProductId(productObjId);
      setListings(updatedListings);

      notifications.show({
        title: t('editProduct.editSuccess'),
        message: isConfirmed ? t('editProduct.listingConfirmed') : t('editProduct.listingDeclined'),
        color: 'green',
      });
    } catch (error) {
      console.error('Error updating listing confirmation:', error);
      notifications.show({
        title: t('editProduct.editError'),
        message: t('editProduct.listingUpdateError'),
        color: 'red',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!productObjId) {
    return (
      <Box className={classes.root} style={accentStyle}>
        <header className={classes.pageHeader}>
          <h2 className={classes.pageTitle}>{t('editProduct.productListings')}</h2>
          <p className={classes.pageDescription}>{t('editProduct.productListingsDescription')}</p>
        </header>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box className={classes.root} style={accentStyle}>
        <header className={classes.pageHeader}>
          <h2 className={classes.pageTitle}>{t('editProduct.productListings')}</h2>
        </header>
        <div className={classes.loadingWrap}>
          <Center>
            <Loader size="sm" color={color} />
          </Center>
        </div>
      </Box>
    );
  }

  const listedAppIds = listings.map((listing) => listing.app._id);
  const availableApps = apps.filter((app) => !listedAppIds.includes(app._id));
  const myApps = availableApps.filter((app) => adminAppIds.has(app._id));
  const publicApps = availableApps.filter((app) => !adminAppIds.has(app._id));

  const renderAppRow = (app: IAppPublic) => (
    <div key={app._id} className={classes.appRow}>
      <div className={classes.appName}>{app.appName}</div>
      <EditorChipButton
        accentColor={color}
        leftIcon={<IconSend size={14} stroke={1.75} />}
        onClick={() => handleRequestListing(app._id)}
      >
        {t('editProduct.requestListing')}
      </EditorChipButton>
    </div>
  );

  return (
    <Box className={classes.root} style={accentStyle}>
      <header className={classes.pageHeader}>
        <h2 className={classes.pageTitle}>{t('editProduct.productListings')}</h2>
      </header>

      {listings.length > 0 && (
        <section className={classes.section}>
          <h3 className={classes.sectionTitle}>{t('editProduct.currentListings')}</h3>
          <div className={classes.cardList}>
            {listings.map((listing) => (
              <div key={listing._id} className={classes.card}>
                <div className={classes.cardMain}>
                  <div className={classes.appName}>{listing.app.appName}</div>
                  <div className={classes.chipRow}>
                    <span className={`${classes.chip} ${getListingStatusChipClass(listing.status)}`}>
                      {listing.status}
                    </span>
                    <span
                      className={`${classes.chip} ${getConfirmationChipClass(listing.appConfirmation)}`}
                    >
                      App: {listing.appConfirmation}
                    </span>
                    <span
                      className={`${classes.chip} ${getConfirmationChipClass(listing.productConfirmation)}`}
                    >
                      Product: {listing.productConfirmation}
                    </span>
                  </div>
                  <div className={classes.metaRow}>
                    <span className={classes.metaItem}>
                      <IconCalendar size={12} stroke={1.75} />
                      {new Date(listing.createdAt).toLocaleDateString()}
                    </span>
                    <span className={classes.metaItem}>
                      <IconClock size={12} stroke={1.75} />
                      {new Date(listing.updatedAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className={classes.cardActions}>
                  {listing.productConfirmation === Listing.Confirmation.CONFIRMED ? (
                    <EditorChipButton
                      variant="danger"
                      leftIcon={<IconTrash size={14} stroke={1.75} />}
                      onClick={() => handleUpdateConfirmation(listing._id, false)}
                    >
                      {t('editProduct.remove')}
                    </EditorChipButton>
                  ) : (
                    <EditorChipButton
                      variant="primary"
                      accentColor={color}
                      leftIcon={<IconCheck size={14} stroke={1.75} />}
                      onClick={() => handleUpdateConfirmation(listing._id, true)}
                    >
                      {t('editProduct.confirm')}
                    </EditorChipButton>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {myApps.length > 0 && (
        <section className={classes.section}>
          <h3 className={classes.sectionTitle}>{t('editProduct.myApps')}</h3>
          <div className={classes.cardList}>{myApps.map(renderAppRow)}</div>
        </section>
      )}

      {publicApps.length > 0 && (
        <section className={classes.section}>
          <h3 className={classes.sectionTitle}>{t('editProduct.publicApps')}</h3>
          <p className={classes.sectionHint}>{t('editProduct.publicAppsHint')}</p>
          <div className={classes.cardList}>{publicApps.map(renderAppRow)}</div>
        </section>
      )}

      {listings.length === 0 && availableApps.length === 0 && (
        <div className={classes.emptyState}>{t('editProduct.noAppsAvailable')}</div>
      )}
    </Box>
  );
}
